import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { PageHeader, Spin, Switch, Tabs } from 'antd';
import PropTypes from 'prop-types';
import { StyledTab, StyledTabBarExtraContent } from './styled';
import {
  ITEM_OPERATIONS,
  TM_FRAUD_SOURCE_IDS,
  trmResources
} from '../../../util/GlobalConstant';
import { returnFraudUserAccessByResource } from '../../../util/TmFraudModuleFilter';
import Summary from './summary/Summary';
import Configuration from './configuration/Configuration';

const { TabPane } = Tabs;

const SOURCE_NAMES = {
  [TM_FRAUD_SOURCE_IDS.TM]: 'TM Detection',
  [TM_FRAUD_SOURCE_IDS.FRAUD]: 'Fraud Detection',
  [TM_FRAUD_SOURCE_IDS.FRAUD_PREV_ID]: 'Fraud Prevention'
};

const DeployInProduction = ({
  match,
  authenticationData,
  singleScenarioData,
  isLoading,
  isLoadingActions
}) => {
  const [isScheduled, setIsScheduled] = useState(false);
  const { scenarioId } = match.params;
  const {
    isFraud: hasFraudAccess,
    isTMuser: hasTMAccess,
    isFraudPrevention: hasFraudPrevAccess
  } = useMemo(
    () =>
      returnFraudUserAccessByResource(
        authenticationData,
        trmResources.APP_3_RESOURCE_UI_AMLS_COMMON_TICKET,
        ITEM_OPERATIONS.CREATE
      ),
    [authenticationData]
  );

  const onSwitchChange = (scheduled) => {
    setIsScheduled(scheduled);
  };

  const isFraudPrevention = useMemo(() => {
    if (singleScenarioData) {
      return singleScenarioData.sourceId === TM_FRAUD_SOURCE_IDS.FRAUD_PREV_ID;
    }

    return false;
  }, [singleScenarioData]);

  const configTabName = useMemo(() => {
    return isLoading
      ? 'Loading...'
      : `${SOURCE_NAMES[singleScenarioData?.sourceId] || ''} Configuration`;
  }, [singleScenarioData, isLoading]);

  const summaryTabName = useMemo(() => {
    return isLoading
      ? 'Loading...'
      : `${SOURCE_NAMES[singleScenarioData?.sourceId] || ''} Summary`;
  }, [singleScenarioData, isLoading]);

  return (
    <>
      <PageHeader
        ghost={false}
        onBack={() => window.history.back()}
        title={`Deploy In Production${scenarioId ? '' : ' Summary'}`}
      />
      <Spin spinning={isLoadingActions}>
        {/* The scenarioId is empty for summary page. And has some value for single scenario page */}
        {scenarioId ? (
          <StyledTab
            tabBarExtraContent={
              isFraudPrevention && (
                <StyledTabBarExtraContent>
                  Scheduled
                  <Switch
                    checkedChildren="Yes"
                    unCheckedChildren="No"
                    onChange={onSwitchChange}
                  />
                </StyledTabBarExtraContent>
              )
            }>
            <TabPane key="1" tab={configTabName}>
              <Configuration
                scenarioId={scenarioId}
                isScheduled={isFraudPrevention ? isScheduled : null}
                isFraudPrevention={isFraudPrevention}
              />
            </TabPane>
            <TabPane key="2" tab={summaryTabName} disabled={isLoading}>
              <Summary
                sourceId={singleScenarioData?.sourceId}
                isScheduled={isFraudPrevention ? isScheduled : null}
                isFraudPrevention={isFraudPrevention}
              />
            </TabPane>
          </StyledTab>
        ) : (
          <StyledTab>
            {(hasFraudAccess || hasTMAccess) && (
              <TabPane tab="Detection" key="Detection">
                <StyledTab>
                  {hasFraudAccess && (
                    <TabPane tab="Fraud" key="1">
                      <Summary sourceId={TM_FRAUD_SOURCE_IDS.FRAUD} />
                    </TabPane>
                  )}
                  {hasTMAccess && (
                    <TabPane tab="TM" key="2">
                      <Summary sourceId={TM_FRAUD_SOURCE_IDS.TM} />
                    </TabPane>
                  )}
                </StyledTab>
              </TabPane>
            )}
            {hasFraudPrevAccess && (
              <TabPane tab="Prevention" key="Prevention">
                <StyledTab
                  tabBarExtraContent={
                    <StyledTabBarExtraContent>
                      Scheduled
                      <Switch
                        checkedChildren="Yes"
                        unCheckedChildren="No"
                        onChange={onSwitchChange}
                      />
                    </StyledTabBarExtraContent>
                  }>
                  <TabPane tab="Fraud" key="3">
                    <Summary
                      sourceId={TM_FRAUD_SOURCE_IDS.FRAUD_PREV_ID}
                      isScheduled={isScheduled}
                      isFraudPrevention={true}
                    />
                  </TabPane>
                </StyledTab>
              </TabPane>
            )}
          </StyledTab>
        )}
      </Spin>
    </>
  );
};

DeployInProduction.propTypes = {
  match: PropTypes.object.isRequired,
  authenticationData: PropTypes.array,
  singleScenarioData: PropTypes.object,
  isLoading: PropTypes.bool,
  isLoadingActions: PropTypes.bool
};

DeployInProduction.defaultProps = {
  isLoading: false,
  isLoadingActions: false
};

const mapStateToProps = ({
  authentication,
  trmDashboard: { singleScenario, challengerActions }
}) => ({
  authenticationData: authentication.data,
  singleScenarioData: singleScenario?.data,
  isLoading: singleScenario?.isLoading,
  isLoadingActions: challengerActions?.isLoading
});

export default connect(mapStateToProps)(DeployInProduction);
